import { UseMutationResult, useMutation, useQueryClient } from '@tanstack/react-query';
import { ImpersonationBroker, asApiError } from '../../../extViewmodels';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { IMeme } from '../../viewModels/gamification';
import { FollowUpOptionIds } from '../../viewModels/phonecall';
import { ILeadFollowUp, ISaveOutcomeRequest, LeadServedSource } from '../../viewModels/queue';
import { queryKeys } from './queryKeys';

export interface SaveCallOutcomeVariables {
	/** Phone call id from the dialer. Pass `'manual'` for a manual (non-dialer) call. */
	phoneCallId: string;
	/** Required when `phoneCallId === 'manual'` so we can hit the lead outcome endpoint. */
	companyId?: string;
	noteId: string;
	outcomeId: string;
	followUp: FollowUpOptionIds | ILeadFollowUp | null;
	doNotContact: boolean;
	source: LeadServedSource;
	/** Optional custom properties passed through to the API (e.g. decision-maker info). */
	customProperties?: Record<string, unknown>;
}

/**
 * POSTs a phone-call outcome, mirroring `PhoneCallViewModel.saveOutcome`.
 *
 * Endpoint selection matches the viewmodel: manual calls (no real Twilio id)
 * hit `/lead/{companyId}/outcome`, real calls hit `/phonecall/{id}/outcome`.
 * Returns the server-provided `IMeme` (or `undefined` when the server omits
 * it) so the caller can surface gamification state.
 *
 * On success the focused-view lead cache is invalidated when we know the
 * company id — manual outcomes always have one; dialer outcomes only have
 * one if the caller passes it explicitly.
 */
export function useSaveCallOutcome(): UseMutationResult<IMeme | undefined, Error, SaveCallOutcomeVariables> {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation<IMeme | undefined, Error, SaveCallOutcomeVariables>({
		mutationFn: async variables => {
			const body: ISaveOutcomeRequest = {
				customProperties: variables.customProperties,
				doNotContact: variables.doNotContact,
				leadServedSource: variables.source,
				noteId: variables.noteId,
				outcomeId: variables.outcomeId,
			};

			const { followUp } = variables;
			if (followUp && Object.values(FollowUpOptionIds).includes(followUp as FollowUpOptionIds)) {
				body.followUpOptionId = followUp as FollowUpOptionIds;
			} else if (followUp === null) {
				body.followUpOptionId = null;
			} else if (followUp && 'scheduled' in (followUp as ILeadFollowUp)) {
				const scheduled = followUp as ILeadFollowUp;
				body.followUpDate = scheduled.followUpDate;
				body.followUpImportanceLevel = scheduled.importance;
				body.followUpName = scheduled.name;
				body.scheduledFollowUp = scheduled.scheduled;
			}

			const isManual = variables.phoneCallId === 'manual';
			if (isManual && !variables.companyId) {
				throw new Error('useSaveCallOutcome: companyId is required for manual outcomes');
			}

			const urlPath = isManual ? `lead/${variables.companyId}/outcome` : `phonecall/${variables.phoneCallId}/outcome`;

			const result = await userSession.webServiceHelper.callWebServiceAsync<{ meme: IMeme }>(
				ImpersonationBroker.composeApiUrl({ urlPath }),
				'POST',
				body
			);

			if (!result.success) {
				throw asApiError(result);
			}

			return result.value?.meme;
		},
		onSuccess: (_meme, variables) => {
			if (variables.companyId) {
				queryClient.invalidateQueries({ queryKey: queryKeys.focusedView.lead(variables.companyId) });
			}
		},
	});
}
